import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaArrowRight, FaCheck } from 'react-icons/fa';

import { ReactComponent as EventWarningIcon } from '../../../../../assets/event-warning-icon.svg';
import GreyHeader from '../../../component/GreyHeader';
import FilterMenu from '../../../component/FilterMenu';
import { CustomButtonToggle } from '../../../component/DropdownComponents';
import CustomButton from '../../../component/CustomButton';
import momenttz from '../../../component/helpers/momenttz';
import { getEventTypeCounts } from '../../../action/ReportsActions';
import { getCompanyById } from '../../../action/CompaniesActions';
import { getDevicesList } from '../../../action/DeviceActions';
import { detectDeviceStatus, getDeviceStatusName } from '../../../component/helpers/helperVehicle';
import { getAvailableFleetStatuses } from '../../../component/helpers/helperSleepMode';
import { dvType } from '../../../component/helpers/constants';
import { slidersIcon } from '../../../component/helpers/iconHelpers';

import '../../../component/FilterMenu.sass';
import './FleetManagerDashboard.sass';
import { getCustomDV7Access, getDV7Access } from '../../../component/helpers/helperDV7Access';

const EVENT_TYPE_LABELS = {
    critical_gsensor: 'High_G-Force', speed: 'Max_Speed', driver: 'Driver', custom: 'Custom',
    harshaccel: 'Harsh_Acceleration', harshbraking: 'Harsh_Braking', adas_ldw: 'Lane_Departure',
    adas_fcw: 'Forward_Collision', adas_hw: 'Headway_Warning', adas_pcw: 'Pedestrian_Warning',
    tailgating: 'Tailgating', distraction: 'Distraction', drowsiness: 'Drowsiness/Head_Down',
    phone_use: 'Phone_Use/Hand_to_Head', yawning: 'Yawning', seatbelt: 'Seatbelt', smoking: 'Smoking',
    camerablock: 'Potential_Ch2_Obstruction', gsensor_wakeup: 'G-Sensor_Wake-Up',
    backup_warning: 'Dangerous_Backing-Up_Alerts', front_vehicle_start: 'Front_Vehicle_Start_Alert',
    trafficlightviolation_saw: 'Signal_Approach', trafficlightviolation_svw: 'Signal_Violation',
    stopsignviolation_rsw: 'Rolling_Stop', stopsignviolation_bsw: 'Bypass_Stop',
    blind_spot_warning_standard: 'Standard_Blind_Spot_Detection_Warning',
    blind_spot_warning_urban: 'Urban_Blind_Spot_Detection_Warning',
    low_speeding: 'Blind_Spot_Detection_Warning_Low', moderate_speeding: 'Blind_Spot_Detection_Warning_Moderate',
    high_speeding: 'Blind_Spot_Detection_Warning_High', severe_speeding: 'Blind_Spot_Detection_Warning_Severe',
};
const RANGES = ['day', 'week', 'month'];
const EVENT_TYPE_API_KEYS = {
    critical_gsensor: ['FatalGSensor'],
    speed: ['HighSpeed'],
    driver: ['External Driver', 'InputSensor2', 'InputSensor3', 'InputSensor5', 'InputSensor9'],
    custom: ['FLEETMANAGER_CLIP', 'SYSTEMADMIN_CLIP', 'ENDUSER_CLIP'],
    distraction: ['Distraction'],
    drowsiness: ['Drowsiness'],
    phone_use: ['PhoneUse'],
    yawning: ['Yawning'],
    seatbelt: ['Seatbelt'],
    smoking: ['Smoking'],
    camerablock: ['CameraBlock'],
    adas_hw: ['HeadwayWarning'],
    harshaccel: ['HarshAccel'],
    harshbraking: ['HarshBraking'],
    adas_ldw: ['LaneDeparture'],
    adas_fcw: ['ForwardCollisionWarning'],
    adas_pcw: ['PedestrianWarning'],
    tailgating: ['FollowingDistanceWarning'],
    gsensor_wakeup: ['GSensorWakeUp'],
    backup_warning: ['BackupWarning'],
    front_vehicle_start: ['FrontVehicleStart'],
    trafficlightviolation_saw: ['TrafficLightViolationSAW'],
    trafficlightviolation_svw: ['TrafficLightViolationSVW'],
    stopsignviolation_rsw: ['StopSignViolationRSW'],
    stopsignviolation_bsw: ['StopSignViolationBSW'],
    blind_spot_warning_standard: ['BlindSpotWarningStandard'],
    blind_spot_warning_urban: ['BlindSpotWarningUrban'],
    low_speeding: ['LowSpeeding'],
    moderate_speeding: ['ModerateSpeeding'],
    high_speeding: ['HighSpeeding'],
    severe_speeding: ['SevereSpeeding'],
};
const STORAGE_KEY = 'dashboardFleetActivityStatusFilters';
const DEFAULT_STATUSES = { driving: true, sleep: true, offline: true, parked: true, idling: true, awake: true, disabled: true };
const STATUS_COLORS = {
    driving: '#3BB273', sleep: '#000000', offline: '#FFFFFF', parked: '#4B3F91',
    idling: '#F5A623', awake: '#4FC3F7', disabled: '#B0B0B0',
};
const EVENT_TYPE_CATEGORY = {
    critical_gsensor: 'safety', speed: 'safety', driver: 'safety', custom: 'safety',
    harshaccel: 'safety', harshbraking: 'safety',
    adas_ldw: 'adas', adas_fcw: 'adas', adas_hw: 'adas', adas_pcw: 'adas', tailgating: 'adas',
    gsensor_wakeup: 'adas', backup_warning: 'adas', front_vehicle_start: 'adas',
    trafficlightviolation_saw: 'adas', trafficlightviolation_svw: 'adas',
    stopsignviolation_rsw: 'adas', stopsignviolation_bsw: 'adas',
    blind_spot_warning_standard: 'adas', blind_spot_warning_urban: 'adas',
    low_speeding: 'adas', moderate_speeding: 'adas', high_speeding: 'adas', severe_speeding: 'adas',
    distraction: 'dms', drowsiness: 'dms', phone_use: 'dms', yawning: 'dms',
    seatbelt: 'dms', smoking: 'dms', camerablock: 'dms',
};

const getDefaultFilters = (DV7access, customDV7Access) => ({
    critical_gsensor: true, speed: true, driver: true, custom: true, harshaccel: true,
    harshbraking: true, adas_fcw: true, adas_ldw: true, ...(!customDV7Access ? { adas_pcw: true } : {}), adas_hw: true,
    tailgating: true, distraction: true, drowsiness: true, phone_use: true, yawning: true,
    seatbelt: true, smoking: true, camerablock: true, feedback: true, reviewed: true,
    pending: true,
    ...(DV7access ? {
        ...(!customDV7Access ? {
            gsensor_wakeup: true, backup_warning: true, front_vehicle_start: true,
            trafficlightviolation_saw: true, trafficlightviolation_svw: true,
        } : {}),
        stopsignviolation_rsw: true, stopsignviolation_bsw: true,
        ...(!customDV7Access ? { blind_spot_warning_standard: true, blind_spot_warning_urban: true } : {}),
        low_speeding: true, moderate_speeding: true, high_speeding: true, severe_speeding: true,
    } : {}),
});

const BarListLabel = ({ itemKey, label }) => {
    const ref = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [label]);

    const content = (
        <span ref={ref} className="bar-list__label">
            {label}
        </span>
    );

    if (!isTruncated) return content;

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`bar-list-tooltip-${itemKey}`}>{label}</Tooltip>}>
            {content}
        </OverlayTrigger>
    );
};

const getRangeDates = (range) => {
    const end = momenttz();
    const start = momenttz().subtract(range === 'day' ? 0 : range === 'week' ? 6 : 29, 'days');
    return {
        start_date: start.format('YYYY-MM-DD'),
        end_date: end.format('YYYY-MM-DD'),
        start_ts: momenttz(start.format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss'),
        end_ts: momenttz(end.format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss'),
    };
};

const FleetManagerDashboard = () => {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'dashboardPage' });
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toggleLoader = (show) => dispatch({ type: 'TOGGLE_LOADER', payload: show });
    const user = useSelector((state) => state.user.user);
    const userRole = user.roles[0];
    const companyId = useSelector((state) => state.company.company.company_id);
    const company = useSelector((state) => state.company.company);
    const DV7access = getDV7Access(company);
    const customDV7Access = getCustomDV7Access(company);

    const [timeRange, setTimeRange] = useState('week');
    const [counts, setCounts] = useState({});
    const [filters, setFilters] = useState(() => {
        const saved = JSON.parse(localStorage.getItem('eventsFilters'));
        return { ...getDefaultFilters(DV7access, customDV7Access), ...saved?.filters };
    });

    const [dataUsage, setDataUsage] = useState(null);
    const [loading, setLoading] = useState(true);

    const [devices, setDevices] = useState([]);
    const [statusFilters, setStatusFilters] = useState(() => {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return { ...DEFAULT_STATUSES, ...saved };
    });

    useEffect(() => {
        if (!companyId) return;
        const { start_ts, end_ts } = getRangeDates(timeRange);
        toggleLoader(true);
        getEventTypeCounts({
            start_ts,
            end_ts,
            company_id: companyId,
            tzname: user.settings?.time_zone,
        })
            .then((res) => {
                const report = res.data.response.report || {};
                const mappedCounts = Object.keys(EVENT_TYPE_API_KEYS).reduce((result, ourKey) => {
                    result[ourKey] = EVENT_TYPE_API_KEYS[ourKey].reduce((sum, apiKey) => sum + (report[apiKey] || 0), 0);
                    return result;
                }, {});
                setCounts(mappedCounts);
            })
            .catch(() => setCounts({}))
            .finally(() => toggleLoader(false));
    }, [companyId, timeRange, userRole]);

    useEffect(() => {
        if (!companyId) return;
        setLoading(true);
        toggleLoader(true);
        getCompanyById(userRole, companyId, 'data_usage')
            .then((res) => {
                setDataUsage(res.data.response.company.data_usage);
                setLoading(false);
            })
            .catch(() => setLoading(false))
            .finally(() => toggleLoader(false));
    }, [companyId, userRole]);

    useEffect(() => {
        if (!companyId) return;
        toggleLoader(true);
        getDevicesList(companyId, 'inactive_devices,ignition')
            .then((res) => {
                const arrDevices = DV7access ? res.data.response.devices : res.data.response.devices.filter((e) => dvType(e) !== 'dv7');
                setDevices(arrDevices.map((device) => ({ ...device, status: detectDeviceStatus(device) })));
            })
            .catch(() => setDevices([]))
            .finally(() => toggleLoader(false));
    }, [companyId, DV7access]);

    const persistEventFilters = (newFilters) => {
        const saved = JSON.parse(localStorage.getItem('eventsFilters')) || {};
        localStorage.setItem('eventsFilters', JSON.stringify({ ...saved, filters: newFilters }));
        setFilters(newFilters);
    };

    const selectAllEventFilters = () => {
        const allSelected = Object.values(filters).every(Boolean);
        const newFilters = Object.keys(filters).reduce((result, key) => {
            result[key] = !allSelected;
            return result;
        }, {});
        persistEventFilters(newFilters);
    };

    const availableFilters = getDefaultFilters(DV7access, customDV7Access);
    const eventItems = Object.keys(EVENT_TYPE_LABELS)
        .filter((key) => key in availableFilters && filters[key] !== false && counts[key] > 0)
        .map((key) => ({
            key,
            label: i18n.t(`constants.eventNames.${EVENT_TYPE_LABELS[key]}`),
            subLabel: t(`eventBreakdown.categories.${EVENT_TYPE_CATEGORY[key]}`),
            value: counts[key],
        }))
        .sort((a, b) => b.value - a.value);

    const availableGB = (dataUsage?.available_data_pool || 0) / 1024 / 1024 / 1024;
    const usedGB = (dataUsage?.total_data_usage || 0) / 1024 / 1024 / 1024;
    const remainingGB = Math.max(availableGB - usedGB, 0);
    const usedPercentage = availableGB ? Math.min((usedGB * 100) / availableGB, 100) : 0;

    const toggleStatus = (status) => {
        const newFilters = { ...statusFilters, [status]: !statusFilters[status] };
        setStatusFilters(newFilters);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
    };

    const selectAllStatuses = () => {
        const allSelected = Object.values(statusFilters).every(Boolean);
        const newFilters = Object.keys(statusFilters).reduce((result, key) => {
            result[key] = !allSelected;
            return result;
        }, {});
        setStatusFilters(newFilters);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
    };

    const statusCounts = devices.reduce((result, device) => {
        result[device.status] = (result[device.status] || 0) + 1;
        return result;
    }, Object.keys(DEFAULT_STATUSES).reduce((result, key) => {
        result[key] = 0;
        return result;
    }, {}));

    const availableStatuses = getAvailableFleetStatuses(statusCounts, 'glance');
    const availableStatusFilters = getAvailableFleetStatuses(statusFilters, 'glance');
    const statusItems = Object.keys(availableStatuses)
        .filter((status) => statusFilters[status])
        .map((status) => {
            const value = availableStatuses[status];
            const statusName = i18n.t(`homePage.fleetStatuses.${getDeviceStatusName(status).replace(' ', '_')}`, getDeviceStatusName(status));
            return { key: status, label: `${statusName} - ${value}`, value, dotColor: STATUS_COLORS[status] };
        })
        .sort((a, b) => b.value - a.value);

    const renderBarList = (items, emptyLabel, { hideValue, withTruncationTooltip, showIcon } = {}) => {
        if (!items.length) return <div className="bar-list__empty">{emptyLabel}</div>;
        const maxValue = Math.max(...items.map(({ value }) => value), 1);
        return (
            <ul className="bar-list">
                {items.map(({ key, label, subLabel, dotColor, value }) => (
                    <li key={key} className="bar-list__row">
                        {showIcon && <span className="bar-list__icon"><EventWarningIcon /></span>}
                        {dotColor && <span className={`bar-list__dot bar-list__dot--${key}`} style={{ background: dotColor }} />}
                        <span className="bar-list__labelcol">
                            {withTruncationTooltip
                                ? <BarListLabel itemKey={key} label={label} />
                                : <span className="bar-list__label" title={label}>{label}</span>}
                            {subLabel && <span className="bar-list__sublabel">{subLabel}</span>}
                        </span>
                        <span className="bar-list__track">
                            <span className="bar-list__fill" style={{ width: `${(value / maxValue) * 100}%` }} />
                        </span>
                        {!hideValue && <span className="bar-list__value">{value}</span>}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div className="fm-home">
            <GreyHeader title={t('title')} />
            <div className="fm-home__tiles">
                <div className="tile tile--tall">
                    <div className="tile__hd">
                        <span className="tile__title">{t('eventBreakdown.title')}</span>
                        <div className="tile__hd-right">
                            <Dropdown align="end" className="tile__range">
                                <Dropdown.Toggle as="button" bsPrefix="tile__range-btn">
                                    {t(`timeRange.${timeRange}`)}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="tile__range-menu">
                                    {RANGES.map((range) => (
                                        <Dropdown.Item key={range} active={range === timeRange} onClick={() => setTimeRange(range)}>
                                            {t(`timeRangeMenu.${range}`)}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                    <div className="tile__bd">
                        {renderBarList(eventItems, t('eventBreakdown.textNoEvents'), { withTruncationTooltip: true, showIcon: true })}
                    </div>
                    <div className="tile__ft">
                        {Object.values(filters).some(Boolean) && eventItems.length > 0 &&
                            <button type="button" className="tile__btn" onClick={() => {
                                const { start_date, end_date } = getRangeDates(timeRange);
                                navigate('/event', { state: { type: 'redirectFromDashboardEventBreakdown', start_date, end_date } });
                            }}>
                                <span className="tile__btn-label">{t('eventBreakdown.btnViewEvents')}</span>
                                <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                            </button>
                        }
                        <div className="tile__ft-right">
                            <FilterMenu
                                userRole={userRole}
                                user={user}
                                filters={filters}
                                fillFilters={(key) => persistEventFilters({ ...filters, [key]: !filters[key] })}
                                selectAllFilters={selectAllEventFilters}
                                DV7access={DV7access}
                                customDV7Access={customDV7Access}
                                toggleIcon={slidersIcon}
                            />
                        </div>
                    </div>
                </div>

                <div className="tile tile--status">
                    <div className="tile__hd">
                        <span className="tile__title">{t('fleetActivityStatus.title')}</span>
                    </div>
                    <div className="tile__bd">
                        <div className="tile__hero-total">
                            {t('fleetActivityStatus.textTotalAssets')}
                            <span className="tile__hero-total-val">{devices.length}</span>
                        </div>
                        {renderBarList(statusItems, t('fleetActivityStatus.textNoStatusesSelected'), { hideValue: true })}
                    </div>
                    <div className="tile__ft">
                        {Object.values(statusFilters).some(Boolean) &&
                            <button type="button" className="tile__btn" onClick={() => {
                                dispatch({
                                    type: 'SET_DASHBOARD_REDIRECT',
                                    payload: { type: 'redirectFromFleetActivityStatus', statuses: statusFilters },
                                });
                                navigate('/');
                            }}>
                                <span className="tile__btn-label">{t('fleetActivityStatus.btnMapView')}</span>
                                <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                            </button>
                        }
                        <div className="tile__ft-right">
                            <Dropdown align="end">
                                <Dropdown.Toggle as={CustomButtonToggle} variant="primary-outline" wrapperClassName="no-caret filter-btn">
                                    {slidersIcon}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="event-filter-menu typeFilterMenu">
                                    <h5 className="category-title">{t('fleetActivityStatus.filterTitle')}</h5>
                                    <CustomButton variant="link" onClick={selectAllStatuses}>
                                        {`${Object.values(statusFilters).every(Boolean) ? i18n.t('eventsPage.FilterMenu.unselect') : i18n.t('eventsPage.FilterMenu.select')} ${i18n.t('eventsPage.FilterMenu.all')}`}
                                    </CustomButton>
                                    <ul className="filters-list">
                                        {Object.keys(availableStatusFilters).map((status) => (
                                            <li key={status} title={i18n.t(`homePage.fleetStatuses.${status.replace(' ', '_')}`, status)} onClick={() => toggleStatus(status)}>
                                                <span className="name">{i18n.t(`homePage.fleetStatuses.${status.replace(' ', '_')}`, status)}</span>
                                                {statusFilters[status] && <FaCheck className="flagSelected" />}
                                            </li>
                                        ))}
                                    </ul>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>

                <div className="tile tile--data-usage">
                    <div className="tile__hd">
                        <span className="tile__title">{t('fleetDataUsage.title')}</span>
                    </div>
                    <div className="tile__bd">
                        {!loading && dataUsage && (
                            <div className="data-usage">
                                <div className="data-usage__donut" style={{ '--used-deg': `${usedPercentage * 3.6}deg` }}>
                                    <div className="data-usage__donut-in">
                                        <div className="data-usage__pct">{`${usedPercentage.toFixed(1)}%`}</div>
                                        <div className="data-usage__pct-label">{t('fleetDataUsage.textUsed')}</div>
                                    </div>
                                </div>
                                <div className="data-usage__legend">
                                    <div className="data-usage__legend-row">
                                        <span className="data-usage__dot used" />
                                        <span className="data-usage__legend-label">{t('fleetDataUsage.textDataUsed')}</span>
                                        <span className="data-usage__legend-value">{`${usedGB.toFixed(1)}GB`}</span>
                                    </div>
                                    <div className="data-usage__legend-row">
                                        <span className="data-usage__dot remaining" />
                                        <span className="data-usage__legend-label">{t('fleetDataUsage.textDataRemaining')}</span>
                                        <span className="data-usage__legend-value">{`${remainingGB.toFixed(1)}GB`}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="tile__total">
                            <span className="tile__total-label">{t('fleetDataUsage.textTotalDataPool')}</span>
                            <span className="tile__total-val">{`${availableGB.toFixed(1)}GB`}</span>
                        </div>
                    </div>
                    <div className="tile__ft">
                        <button type="button" className="tile__btn" onClick={() => navigate('/managevehicles')}>
                            <span className="tile__btn-label">{t('fleetDataUsage.btnGoToAssets')}</span>
                            <span className="tile__btn-icon"><FaArrowRight size={18} /></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FleetManagerDashboard;
